import { useMemo, useState } from "react";
import { Navigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useMember } from "@/contexts/memberContext";
import { OrganisationService } from "@/services/OrganisationService";
import { isOrganisationOwner } from "@/utils/organisationOwner";
import OrgMembersList from "@/components/tpr/Organisation/OrgMembersList";
import OrgBookingsList from "@/components/tpr/Organisation/OrgBookingsList";
import OrgBillingView from "@/components/tpr/Organisation/OrgBillingView";
import type { Member } from "@/types/Member";

type Tab = "members" | "bookings" | "billing";

/**
 * Owner-facing organisation screen at /organisation.
 *
 * Resolves the owner's org id from the member record, fetches org details +
 * members via checkIsOwnerAndGetMembers, and renders a tabbed layout:
 *   Members | Bookings | Billing (Billing is a placeholder until Phase 4)
 *
 * Non-owners are redirected to /dashboard.
 */
export default function OrganisationView() {
  const { member } = useMember();
  const [activeTab, setActiveTab] = useState<Tab>("members");

  const orgId = member?.ownerOfOrganisationId || member?.organisation?.id;

  const organisationService = useMemo(() => new OrganisationService(), []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["organisation-owner-view", orgId],
    queryFn: () => organisationService.checkIsOwnerAndGetMembers(orgId!),
    enabled: !!orgId && isOrganisationOwner(member),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Redirect non-owners
  if (member !== undefined && !isOrganisationOwner(member)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect if no org can be resolved once member is loaded
  if (member !== undefined && !orgId) {
    return <Navigate to="/dashboard" replace />;
  }

  const org = data?.organisation;

  // Cast OrganisationMember[] to Member[] — OrgMembersList only uses the overlapping fields
  // (id, firstName, lastName, email) plus ownerId comparison.
  const members = (data?.members ?? []) as unknown as Member[];

  const tabClass = (tab: Tab) =>
    `px-4 py-2 text-sm font-medium border-b-2 transition-colors hover:cursor-pointer ${
      activeTab === tab
        ? "border-blue-600 text-blue-600"
        : "border-transparent text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="container mx-auto py-8 max-w-4xl px-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight break-words">
          {isLoading ? "Organisation" : (org?.name ?? "Organisation")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your organisation members, bookings, and billing.
        </p>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-border mb-6" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === "members"}
          className={tabClass("members")}
          onClick={() => setActiveTab("members")}
        >
          Members
        </button>
        <button
          role="tab"
          aria-selected={activeTab === "bookings"}
          className={tabClass("bookings")}
          onClick={() => setActiveTab("bookings")}
        >
          Bookings
        </button>
        <button
          role="tab"
          aria-selected={activeTab === "billing"}
          className={tabClass("billing")}
          onClick={() => setActiveTab("billing")}
        >
          Billing
        </button>
      </div>

      {/* Tab content */}
      {activeTab === "members" && (
        <div role="tabpanel">
          {isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading members…</div>
          ) : isError ? (
            <div className="py-8 text-center text-sm text-destructive">Failed to load organisation.</div>
          ) : (
            <OrgMembersList members={members} ownerId={member?.id} />
          )}
        </div>
      )}

      {activeTab === "bookings" && orgId && (
        <div role="tabpanel">
          <OrgBookingsList organisationId={orgId} />
        </div>
      )}

      {activeTab === "billing" && orgId && (
        <div role="tabpanel" data-testid="org-billing-tab">
          <OrgBillingView organisationId={orgId} />
        </div>
      )}
    </div>
  );
}
